import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { UsageBadge } from "@/components/usage-badge";
import { Button } from "@/components/ui/button";
import { GenerateCta } from "@/components/generate-cta";
import { BRAND_NAME } from "@/lib/brand";

const mobileLinkClass =
  "block px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground";

export function SiteNav() {
  const { user, supabase } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    navigate({ to: "/" });
  };

  return (
    <nav className="no-print sticky top-0 z-40 w-full border-b border-border/60 bg-background/85 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2 text-lg font-bold tracking-tight">
          <span className="text-xl" aria-hidden>
            ✍️
          </span>
          <span>{BRAND_NAME}</span>
        </Link>
        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
          {user ? (
            <>
              <UsageBadge compact />
              <Link
                to="/guides"
                className="hidden shrink-0 text-sm font-medium text-muted-foreground hover:text-foreground sm:inline"
              >
                Guides
              </Link>
              <Link
                to="/pricing"
                className="hidden shrink-0 text-sm font-medium text-muted-foreground hover:text-foreground sm:inline"
              >
                Pricing
              </Link>
              <Link
                to="/dashboard"
                className="hidden text-sm font-medium text-muted-foreground hover:text-foreground sm:inline"
              >
                Dashboard
              </Link>
              <Link
                to="/settings"
                className="hidden text-sm font-medium text-muted-foreground hover:text-foreground sm:inline"
              >
                Settings
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
                Log out
              </Button>
              <Button asChild size="sm">
                <Link to="/generate">New Document</Link>
              </Button>
            </>
          ) : (
            <>
              <Link
                to="/guides"
                className="hidden shrink-0 text-sm font-medium text-muted-foreground hover:text-foreground sm:inline"
              >
                Guides
              </Link>
              <Link
                to="/pricing"
                className="hidden shrink-0 text-sm font-medium text-muted-foreground hover:text-foreground sm:inline"
              >
                Pricing
              </Link>
              <Link
                to="/login"
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                Login
              </Link>
              <GenerateCta size="sm">Get Started Free</GenerateCta>
            </>
          )}
          <div className="relative sm:hidden">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav-menu"
              type="button"
              data-testid="mobile-nav-toggle"
              onClick={() => setMobileOpen((open) => !open)}
            >
              <Menu />
            </Button>
            {mobileOpen ? (
              <div
                id="mobile-nav-menu"
                data-testid="mobile-nav-menu"
                className="absolute right-0 top-full z-50 mt-2 min-w-44 rounded-md border border-border bg-background py-1 shadow-lg"
              >
                <Link to="/guides" className={mobileLinkClass} onClick={() => setMobileOpen(false)}>
                  Guides
                </Link>
                <Link to="/pricing" className={mobileLinkClass} onClick={() => setMobileOpen(false)}>
                  Pricing
                </Link>
                {user ? (
                  <>
                    <Link
                      to="/dashboard"
                      className={mobileLinkClass}
                      onClick={() => setMobileOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/settings"
                      className={mobileLinkClass}
                      onClick={() => setMobileOpen(false)}
                    >
                      Settings
                    </Link>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </nav>
  );
}
